import { logger } from '@zep/core-logger';

import { useLocale } from '@/hooks';
import { SchoolMapper } from '@/shared/category/util';
import { useIsInGoogleClassroom } from '@/shared/googleClassroom';
import { type EventProperties } from '@/utils/analytics';
import { useBuildInfoContext } from '@/views/build/context';
import { getGradeNumber } from '@/views/build/util';
import { getAddedQuizBankItemIds } from '@/views/build/util/getAddedQuizBankItemIds';

import { useSelectSubject } from '../components/SectionBasicInfo/useSelectSubject';
import { useAiBuilderChatSessionIdSafe } from '../features/ai-builder-chat/stores';
import { useBuildFormContext } from '../form/schema';
import { type BuildQuizListItem } from '../form/schema/BuildQuizListSchema';

import { getIsMapTemplateChanged } from './getIsMapTemplateChanged';

/**
 * quizList에서 AI 생성 origin의 generation_id들만 distinct로 추출.
 *
 * ClickHouse 분석 시 server biz_generation_assignment.generation_id와 join하기 위한 키.
 * H5 funnel(generation → save → quiz_set 발행) strict attribution용.
 */
const extractAiGenerationIds = (quizList: BuildQuizListItem[]): string[] => {
  const ids = quizList
    .map(quiz => quiz.origin?.generationId)
    .filter((id): id is string => Boolean(id));
  return Array.from(new Set(ids));
};

export const useBuildEventTrack = () => {
  const { getValues } = useBuildFormContext();
  const { locale } = useLocale();
  const { subjectName, subSubjectName } = useSelectSubject();
  const { schoolInfoTree, mapTemplates, action, originQuizSet } =
    useBuildInfoContext();
  const isInGoogleClassroom = useIsInGoogleClassroom();
  const aiBuilderSessionId = useAiBuilderChatSessionIdSafe();
  const { quizList } = getValues();

  const isDuplicate = action === 'DUPLICATE';
  const isUpdated = action === 'UPDATE';

  return (
    quizSetId: string,
    countryCode: string,
    timeInfo?: {
      mutationCompleteDuration?: string;
      buildPageExitDuration?: string;
    },
  ) => {
    const {
      isPublic,
      isAllowPass,
      answerRevealMode,
      materials,
      title,
      isLastSchoolInfoSaved,
      templateId,
      publisher,
      customPublisher,
      semesterId,
      gradeId,
      schoolTypeId,
      isNextQuizsetAvailable,
      nextQuizsetId,
      aiTranslationLanguages,
      isAiTranslationEnabled,
      aiTutor,
    } = getValues();
    const schoolType = schoolInfoTree.children.find(
      d => d.categoryTreeId === schoolTypeId,
    );
    const selectedTemplate = mapTemplates.find(
      d => d.templateId === templateId,
    );
    const finalIsNextQuizsetAvailable = !nextQuizsetId
      ? false
      : isNextQuizsetAvailable;

    // AI 생성 lineage: 이 quiz_set의 어느 문항들이 어떤 AI generation에서 비롯됐는지 distinct로 추출.
    // ClickHouse 분석 시 server biz_generation_assignment.generation_id와 join → H5 funnel strict 추적.
    const aiGenerationIds = extractAiGenerationIds(quizList);

    // 문제 은행 출처 문항 수. 담은 뒤 고쳐도 `bankItemId` 는 남으므로 "은행에서 왔다"는 축이다.
    // 은행 퍼널(`quiz_bank_problem_import_submit`)과 세션 join 없이 최종 퀴즈의 은행 비중을 읽는다.
    const bankProblemCount = getAddedQuizBankItemIds(quizList).length;

    const eventProperties: EventProperties = {
      quizSetId,
      title,
      isPublic,
      isAllowPass,
      answer_reveal_mode: answerRevealMode,
      templateId,
      countryCode,
      subject: subjectName,
      subSubject: subSubjectName,
      location: locale,
      quizNumber: quizList.length,
      use_next_time: isLastSchoolInfoSaved,
      template_name: selectedTemplate?.name,
      map_type: selectedTemplate?.type,
      map_plan: selectedTemplate?.templatePlanType,
      interest_1: schoolType?.name || 'UNKNOWN',
      ...(gradeId && { interest_2: getGradeNumber(gradeId) }),
      ...(semesterId && { interest_3: semesterId }),
      ...(publisher && {
        textbookPublisher: SchoolMapper.isEtc(publisher)
          ? customPublisher
          : publisher,
      }),
      hasLearningMaterial: 0 < materials.length,
      ...(isDuplicate && { isDuplicate }),
      isNextQuizsetAvailable: finalIsNextQuizsetAvailable,
      nextQuizset: finalIsNextQuizsetAvailable
        ? { id: nextQuizsetId }
        : undefined,
      originQuizSetId: originQuizSet?.originQuizSetId,
      ai_generation_ids: aiGenerationIds,
      has_bank_problem: 0 < bankProblemCount,
      bank_problem_count: bankProblemCount,
      // AI builder chat 세션 attribution — lesson-ready 퍼널의 session→save join 키.
      ...(aiBuilderSessionId && { ai_builder_session_id: aiBuilderSessionId }),
    };

    if (isUpdated) {
      eventProperties.is_map_template_changed = getIsMapTemplateChanged({
        action,
        originTemplateId: originQuizSet?.templateId,
        templateId,
      });
    }

    if (isInGoogleClassroom) {
      eventProperties.extension = 'google_classroom';
    }

    // aiTranslation 이벤트 추적
    if (!isAiTranslationEnabled || !aiTranslationLanguages.length) {
      eventProperties.aiTranslation = false;
    } else {
      eventProperties.aiTranslationLocale1 = aiTranslationLanguages[0] ?? '';
      eventProperties.aiTranslationLocale2 = aiTranslationLanguages[1] ?? '';
      eventProperties.aiTranslationLocale3 = aiTranslationLanguages[2] ?? '';
    }

    if (aiTutor?.id) {
      eventProperties.hasAiTutor = true;
      eventProperties.aiTutorId = aiTutor.id;
      eventProperties.aiTutorName = aiTutor.name;
    }
    if (timeInfo) {
      eventProperties.responseDuration = timeInfo.mutationCompleteDuration;
      eventProperties.routeChangeDuration = timeInfo.buildPageExitDuration;
    }

    // create_complete 이벤트에 create_type 속성 추가 (duplicate_complete 이벤트 통합)
    if (!isUpdated) {
      eventProperties.create_type = isDuplicate ? 'duplicate' : 'create';
    }

    logger.track(
      isUpdated ? 'edit_complete' : 'create_complete',
      eventProperties,
    );
  };
};
